import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { getSafeSkillName, processSkillsData } from '../../utils/skillsDataParser';
import { sanitizeSkillName } from '../../utils/skillsUtils';
import { 
  findDuplicateSkills, 
  processAndMergeDuplicates, 
  getDuplicateStats,
  areSkillsSimilar,
  normalizeSkillName
} from '../../utils/skillsMerger';
import { Helmet as _Helmet } from 'react-helmet';
import _VAHeaderNav from '../../components/navigation/VAHeaderNav';
import _Icon from '../../components/AppIcon';
import _Button from '../../components/ui/Button';
import _Input from '../../components/ui/Input';
import { useAuth } from '../../hooks/useAuth';
import { useModal } from '../../hooks/useModal';
import InfoModal from '../../components/ui/InfoModal';
import { PROFILE_CONSTANTS } from '../va-profile-settings/constants/profileConstants';
import AIAssessmentModal from './components/AIAssessmentModal';

// Star Rating Component - Memoized for performance
const StarRating = React.memo(({ rating, onRatingChange, readOnly = false }) => {
  const [hoverRating, setHoverRating] = useState(0);
  
  const handleMouseEnter = useCallback((star) => {
    if (!readOnly) setHoverRating(star);
  }, [readOnly]);
  
  const handleMouseLeave = useCallback(() => {
    if (!readOnly) setHoverRating(0);
  }, [readOnly]);
  
  const handleClick = useCallback((star) => {
    if (!readOnly) onRatingChange(star);
  }, [readOnly, onRatingChange]);
  
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          disabled={readOnly}
          className={`${readOnly ? 'cursor-default' : 'cursor-pointer hover:scale-110'}`}
          onMouseEnter={() => handleMouseEnter(star)}
          onMouseLeave={handleMouseLeave}
          onClick={() => handleClick(star)}
        >
          <_Icon 
            name="Star" 
            size={16} 
            className={`${
              star <= (hoverRating || rating) 
                ? 'text-warning fill-current' 
                : 'text-gray-300'
            }`}
          />
        </button>
      ))}
    </div>
  );
});

// Skill Card Component - Optimized with React.memo
// Skill Card Component - Memoized for performance
const SkillCard = React.memo(({ 
  skill, 
  category, 
  isAdded, 
  userRating, 
  userExperience, 
  onAddSkill, 
  onRateSkill, 
  onUpdateExperience,
  canAdd,
  validationMessage,
  hasIssues,
  isCustom = false // New prop to indicate custom skills
}) => {
  const [showExperienceField, setShowExperienceField] = useState(false);
  const [experienceText, setExperienceText] = useState(userExperience || '');
  const [showAIAssessment, setShowAIAssessment] = useState(false);

  // Update experienceText when userExperience changes
  useEffect(() => {
    setExperienceText(userExperience || '');
  }, [userExperience]);

  // Memoize validation checks
  const needsExperience = useMemo(() => 
    userRating >= 4 && (!userExperience || userExperience.length < 180), 
    [userRating, userExperience]
  );
  
  const showValidationError = useMemo(() => 
    needsExperience && isAdded, 
    [needsExperience, isAdded]
  );

  // Memoize category icon and color functions
  const getCategoryIcon = useMemo(() => {
    const icons = {
      'Administrative': 'FileText',
      'Technical': 'Code',
      'Creative': 'Palette',
      'Communication': 'MessageCircle',
      'Marketing': 'TrendingUp',
      'Project Management': 'Calendar',
      'Data Analysis': 'BarChart',
      'Finance': 'DollarSign'
    };
    return icons[category] || 'Star';
  }, [category]);

  const getCategoryColor = useMemo(() => {
    const colors = {
      'Administrative': 'bg-blue-50 text-blue-700 border-blue-200',
      'Technical': 'bg-green-50 text-green-700 border-green-200',
      'Creative': 'bg-purple-50 text-purple-700 border-purple-200',
      'Communication': 'bg-orange-50 text-orange-700 border-orange-200',
      'Marketing': 'bg-pink-50 text-pink-700 border-pink-200',
      'Project Management': 'bg-indigo-50 text-indigo-700 border-indigo-200',
      'Data Analysis': 'bg-cyan-50 text-cyan-700 border-cyan-200',
      'Finance': 'bg-emerald-50 text-emerald-700 border-emerald-200'
    };
    return colors[category] || 'bg-gray-50 text-gray-700 border-gray-200';
  }, [category]);

  // Memoize event handlers
  const handleAddSkill = useCallback(() => {
    onAddSkill(skill, category);
  }, [onAddSkill, skill, category]);

  const handleRateSkill = useCallback((rating) => {
    onRateSkill(skill, rating);
  }, [onRateSkill, skill]);

  const handleUpdateExperience = useCallback((experience) => {
    onUpdateExperience(skill, experience);
  }, [onUpdateExperience, skill]);

  const handleToggleExperience = useCallback(() => {
    setShowExperienceField(!showExperienceField);
  }, [showExperienceField]);

  const handleCancelExperience = useCallback(() => {
    setShowExperienceField(false);
    setExperienceText(userExperience || '');
  }, [userExperience]);

  const handleSaveExperience = useCallback(() => {
    handleUpdateExperience(experienceText);
    setShowExperienceField(false);
  }, [handleUpdateExperience, experienceText]);

  const handleStartAIAssessment = useCallback(() => {
    setShowAIAssessment(true);
  }, []);

  const handleCloseAIAssessment = useCallback(() => {
    setShowAIAssessment(false);
  }, []);

  const handleAssessmentComplete = useCallback((result) => {
    if (process.env.NODE_ENV === 'development') {
      console.log('[SkillCard] Assessment completed:', result);
    }
  }, []);

  // Check if AI Verify button should be shown
  const showAIVerifyButton = useMemo(() => {
    return isAdded && userRating >= 3 && userExperience && userExperience.length >= 180;
  }, [isAdded, userRating, userExperience]);

  return (
    <div className={`bg-white rounded-xl p-6 border-2 transition-all duration-200 hover:shadow-md ${
      hasIssues ? 'border-red-300 bg-red-50' : 
      isAdded ? 'border-primary bg-primary-50' : 'border-border'
    }`}>
      {/* Category Badge */}
      <div className="flex justify-between items-center mb-3">
        <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getCategoryColor}`}>
          <_Icon name={getCategoryIcon} size={12} />
          {category}
        </div>
        
        {/* Custom Skill Badge */}
        {isCustom && (
          <div className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-700 border-purple-200">
            <_Icon name="Star" size={12} />
            Custom
          </div>
        )}
      </div>
      
      {/* Skill Name */}
      <h3 className="text-lg font-semibold text-text-primary mb-2">{skill}</h3>
      
      {/* Description */}
      <p className="text-sm text-text-secondary mb-4">
        Essential skill for {category.toLowerCase()} tasks and responsibilities.
      </p>
      
      {/* Rating Section */}
      <div className="mb-4">
        <p className="text-sm font-medium text-text-primary mb-2">Rate your proficiency:</p>
        <StarRating 
          rating={userRating || 0} 
          onRatingChange={handleRateSkill}
          readOnly={!isAdded}
        />
        {userRating > 0 && (
          <p className="text-xs text-text-muted mt-1">
            {userRating === 1 ? 'Beginner' : 
             userRating === 2 ? 'Basic' :
             userRating === 3 ? 'Intermediate' :
             userRating === 4 ? 'Advanced' : 'Expert'}
          </p>
        )}
      </div>
      
      {/* Experience Section - Only show if skill is added */}
      {isAdded && (
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium text-text-primary">Brief Experience:</p>
            <button
              type="button"
              onClick={handleToggleExperience}
              className="text-xs text-primary hover:text-primary-dark"
            >
              {showExperienceField ? 'Hide' : (userExperience ? 'Edit' : 'Add')}
            </button>
          </div>
          
          {userExperience && !showExperienceField && (
            <div className="bg-gray-50 rounded-lg p-3 border">
              <p className="text-sm text-text-secondary">{userExperience}</p>
            </div>
          )}
          
          {showExperienceField && (
            <div className="space-y-2">
              <textarea
                value={experienceText}
                onChange={(e) => setExperienceText(e.target.value)}
                placeholder="Describe your experience with this skill (max 300 characters)..."
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
                rows={3}
                maxLength={300}
              />
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-500">
                  {experienceText.length}/300 characters
                </span>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={handleCancelExperience}
                    className="px-2 py-1 text-xs text-gray-600 hover:text-gray-800 "
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleSaveExperience}
                    className="px-3 py-1 text-xs bg-primary text-white rounded hover:bg-primary-dark "
                  >
                    Save
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
      
      {/* Validation Error Message */}
      {showValidationError && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-start gap-2">
            <_Icon name="AlertCircle" size={16} className="text-red-500 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-red-800 mb-1">Experience Required</p>
              <p className="text-xs text-red-700">
                Skills rated 4-5 stars require at least 180 characters of experience before adding to your profile.
              </p>
              <p className="text-xs text-red-600 mt-1">
                Current: {userExperience?.length || 0}/180 characters
              </p>
            </div>
          </div>
        </div>
      )}
      
      {/* AI Verify Button - shown when skill is added, rated 3+, and has experience */}
      {showAIVerifyButton && (
        <_Button
          variant="primary"
          size="sm"
          fullWidth
          onClick={handleStartAIAssessment}
          iconName="Sparkles"
          className="mb-2 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700"
        >
          Verify Skill
        </_Button>
      )}
      
      {/* Action Button */}
      <_Button
        variant={isAdded ? 'secondary' : 'primary'}
        size="sm"
        fullWidth
        onClick={handleAddSkill}
        iconName={isAdded ? 'Check' : 'Plus'}
        disabled={!canAdd && !isAdded}
      >
        {isAdded ? 'Added to Profile' : 'Add to Profile'}
      </_Button>
      
      {/* AI Assessment Modal */}
      {showAIAssessment && (
        <AIAssessmentModal
          isOpen={showAIAssessment}
          onClose={handleCloseAIAssessment}
          skill={{ name: skill }}
          category={category}
          onAssessmentComplete={handleAssessmentComplete}
        />
      )}
      
      {/* Validation Message for non-added skills */}
      {!isAdded && validationMessage && (
        <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-700">
          <_Icon name="Info" size={12} className="inline mr-1" />
          {validationMessage}
        </div>
      )}
    </div>
  );
});

// Compact List View Component - Optimized with React.memo
const SkillListItem = React.memo(({ skill, category, isAdded, userRating, userExperience, onAddSkill, onRateSkill, onUpdateExperience, canAdd, validationMessage, hasIssues, isCustom = false }) => {
  const [showExperienceField, setShowExperienceField] = useState(false);
  const [experienceText, setExperienceText] = useState(userExperience || '');

  // Update experienceText when userExperience changes
  useEffect(() => {
    setExperienceText(userExperience || '');
  }, [userExperience]);

  // Memoize validation checks
  const needsExperience = useMemo(() => 
    userRating >= 4 && (!userExperience || userExperience.length < 180), 
    [userRating, userExperience]
  );
  
  const showValidationError = useMemo(() => 
    needsExperience && isAdded, 
    [needsExperience, isAdded]
  );

  // Memoize category icon
  const getCategoryIcon = useMemo(() => {
    const icons = {
      'Administrative': 'FileText',
      'Technical': 'Code',
      'Creative': 'Palette',
      'Communication': 'MessageCircle',
      'Marketing': 'TrendingUp',
      'Project Management': 'Calendar',
      'Data Analysis': 'BarChart',
      'Finance': 'DollarSign'
    };
    return icons[category] || 'Star';
  }, [category]);

  // Memoize event handlers
  const handleAddSkill = useCallback(() => {
    onAddSkill(skill, category);
  }, [onAddSkill, skill, category]);

  const handleRateSkill = useCallback((rating) => {
    onRateSkill(skill, rating);
  }, [onRateSkill, skill]);

  const handleToggleExperience = useCallback(() => {
    setShowExperienceField(!showExperienceField);
  }, [showExperienceField]);

  const handleCancelExperience = useCallback(() => {
    setShowExperienceField(false);
    setExperienceText(userExperience || '');
  }, [userExperience]);

  const handleSaveExperience = useCallback(() => {
    onUpdateExperience(skill, experienceText);
    setShowExperienceField(false);
  }, [onUpdateExperience, skill, experienceText]);

  return (
    <div className={`border rounded-lg transition-all duration-200 hover:shadow-sm ${
      hasIssues ? 'border-red-300 bg-red-50' :
      isAdded ? 'border-primary bg-primary-50' : 'border-gray-200 bg-white hover:border-gray-300'
    }`}>
      {/* Main Row */}
      <div className="flex items-center justify-between p-4">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          {/* Category Icon */}
          <div className="flex-shrink-0">
            <_Icon name={getCategoryIcon} size={16} className="text-gray-500" />
          </div>
          
          {/* Skill Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h4 className="font-medium text-text-primary truncate">{skill}</h4>
              {isCustom && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                  <_Icon name="Star" size={10} />
                  Custom
                </span>
              )}
            </div>
            <p className="text-sm text-text-muted">{category}</p>
          </div>
          
          {/* Rating */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <StarRating 
              rating={userRating || 0} 
              onRatingChange={handleRateSkill}
              readOnly={!isAdded}
            />
            {userRating > 0 && (
              <span className="text-xs text-gray-500 min-w-0 hidden sm:block">
                {userRating === 1 ? 'Beginner' : 
                 userRating === 2 ? 'Basic' :
                 userRating === 3 ? 'Intermediate' :
                 userRating === 4 ? 'Advanced' : 'Expert'}
              </span>
            )}
          </div>
          
          {/* Experience Toggle Button */}
          {isAdded && (
            <div className="flex-shrink-0">
              <button
                type="button"
                onClick={handleToggleExperience}
                className="px-2 py-1 text-xs text-gray-600 hover:text-gray-800 border border-gray-300 rounded hover:bg-gray-50 "
              >
                {userExperience ? 'Experience' : 'Add Exp'}
              </button>
            </div>
          )}
          
          {/* Toggle Button */}
          <div className="flex-shrink-0 ml-2">
            <_Button
              variant={isAdded ? 'secondary' : 'primary'}
              size="sm"
              onClick={handleAddSkill}
              iconName={isAdded ? 'Check' : 'Plus'}
              disabled={!canAdd && !isAdded}
            >
              <span className="hidden sm:inline">
                {isAdded ? 'Added' : 'Add'}
              </span>
            </_Button>
          </div>
        </div>
      </div>
      
      {/* Validation Error Row */}
      {showValidationError && (
        <div className="px-4 py-3 bg-red-50 border-t border-red-200">
          <div className="flex items-start gap-2">
            <_Icon name="AlertCircle" size={14} className="text-red-500 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-xs font-medium text-red-800 mb-1">Experience Required</p>
              <p className="text-xs text-red-700">
                Skills rated 4-5 stars need 180+ characters of experience. Current: {userExperience?.length || 0}/180
              </p>
            </div>
          </div>
        </div>
      )}
      
      {/* Validation Message for non-added skills */}
      {!isAdded && validationMessage && (
        <div className="px-4 py-2 bg-amber-50 border-t border-amber-200">
          <div className="flex items-center gap-1">
            <_Icon name="Info" size={12} className="text-amber-600" />
            <p className="text-xs text-amber-700">{validationMessage}</p>
          </div>
        </div>
      )}
      
      {/* Experience Section */}
      {isAdded && (userExperience || showExperienceField) && (
        <div className="px-4 pb-4 border-t border-gray-200">
          {userExperience && !showExperienceField && (
            <div className="pt-3">
              <p className="text-xs font-medium text-gray-700 mb-1">Experience:</p>
              <p className="text-sm text-gray-600">{userExperience}</p>
            </div>
          )}
          
          {showExperienceField && (
            <div className="pt-3 space-y-2">
              <textarea
                value={experienceText}
                onChange={(e) => setExperienceText(e.target.value)}
                placeholder="Describe your experience with this skill (max 300 characters)..."
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
                rows={2}
                maxLength={300}
              />
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-500">
                  {experienceText.length}/300 characters
                </span>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={handleCancelExperience}
                    className="px-2 py-1 text-xs text-gray-600 hover:text-gray-800 "
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleSaveExperience}
                    className="px-3 py-1 text-xs bg-primary text-white rounded hover:bg-primary-dark "
                  >
                    Save
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
});

// Main Component
const UpSkillsPage = () => {
  const { userProfile, updateProfile } = useAuth();
  const { infoModal, showInfo, closeInfo } = useModal();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [userSkills, setUserSkills] = useState([]);
  const [showCustomSkillModal, setShowCustomSkillModal] = useState(false);
  const [customSkill, setCustomSkill] = useState({ name: '', category: 'Administrative' });
  const [sortBy, setSortBy] = useState('name'); // 'name', 'category', 'rating', 'added'
  const [sortOrder, setSortOrder] = useState('asc'); // 'asc', 'desc'
  const [filterBy, setFilterBy] = useState('all'); // 'all', 'added', 'notAdded', 'rated', 'unrated'
  const [viewMode, setViewMode] = useState('grid'); // 'grid', 'list'
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [duplicateStats, setDuplicateStats] = useState(null);
  const [duplicateGroups, setDuplicateGroups] = useState([]);
  const [mergingInProgress, setMergingInProgress] = useState(false);

  // Ref to track if notifications have been shown to prevent infinite loops
  const notificationsShownRef = useRef({
    dataFixed: false,
    dataError: false,
    highRatingInfo: false,
    duplicatesDetected: false
  });
  
  // Initialize user skills from profile with enhanced data cleaning and error handling
  useEffect(() => {
    if (userProfile?.skills) {
      try {
        // Process skills data to clean and normalize
        const { skills: normalizedSkills, hasRawJsonIssues, stats } = processSkillsData(userProfile.skills);
        
        // Log stats if there were issues found
        if (hasRawJsonIssues) {
          console.log('Fixed raw JSON issues in skills data during initialization:', stats);
          
          // Show notification about fixed data issues if significant and not already shown
          if (stats.recoveredFromRawJson > 0 && !notificationsShownRef.current.dataFixed) {
            // Mark as shown to prevent infinite loop
            notificationsShownRef.current.dataFixed = true;
            
            // Use setTimeout to avoid state updates during render
            setTimeout(() => {
              showInfo({
                title: 'Skills Data Fixed',
                message: `Fixed ${stats.recoveredFromRawJson} skills with data format issues. Your skills have been recovered successfully.`,
                type: 'info',
                buttonLabel: 'Continue'
              });
            }, 0);
          }
        }
        
        // Convert normalized skills to the format expected by this component
        const skillsWithRatings = normalizedSkills.map((skill, index) => {
          // Generate a unique ID if not present
          const skillId = skill.id || `skill-${index}-${Math.random().toString(36).substr(2, 5)}`;
          const skillName = getSafeSkillName(skill);
          const skillCategory = typeof skill.category === 'string' ? skill.category : 'Administrative';
          const skillRating = typeof skill.rating === 'number' ? skill.rating : 3;
          const skillExperience = typeof skill.experience === 'string' ? skill.experience : '';
          
          // Check if this is a high-rated skill with insufficient experience
          const needsMoreExperience = skillRating >= 4 && skillExperience.length < 180;
          
          return {
            id: skillId,
            name: skillName,
            category: skillCategory,
            rating: skillRating,
            experience: skillExperience,
            type: skill.type || 'upskills', // Mark as upskills format for future processing
            hasValidationIssue: needsMoreExperience
          };
        });
        
        setUserSkills(skillsWithRatings);
        
        // Check for validation issues and notify user if needed
        const validationIssues = skillsWithRatings.filter(s => s.hasValidationIssue);
        if (validationIssues.length > 0) {
          console.log(`Found ${validationIssues.length} skills with validation issues:`, validationIssues);
        }
      } catch (error) {
        console.error('Error initializing skills data:', error);
        
        // Show error notification if not already shown
        if (!notificationsShownRef.current.dataError) {
          // Mark as shown to prevent infinite loop
          notificationsShownRef.current.dataError = true;
          
          // Use setTimeout to avoid state updates during render
          setTimeout(() => {
            showInfo({
              title: 'Skills Data Error',
              message: 'There was an error loading your skills. Some data may be missing or incomplete.',
              type: 'warning'
            });
          }, 0);
        }
      }
    }
  }, [userProfile, showInfo]);

  // Check for duplicates whenever userSkills change
  useEffect(() => {
    if (userSkills && userSkills.length > 0) {
      const stats = getDuplicateStats(userSkills);
      const groups = findDuplicateSkills(userSkills);
      setDuplicateStats(stats);
      setDuplicateGroups(groups);
      
      // Log duplicate information for debugging
      if (stats.duplicateGroups > 0) {
        console.log('Duplicate skills detected:', {
          stats,
          groups: groups.map(group => ({
            skillNames: group.map(item => getSafeSkillName(item.skill)),
            count: group.length
          }))
        });
        
        // Show notification about duplicates found (only once per session)
        if (!notificationsShownRef.current.duplicatesDetected) {
          notificationsShownRef.current.duplicatesDetected = true;
          
          setTimeout(() => {
            showInfo({
              title: 'Duplicate Skills Detected',
              message: `Found ${stats.duplicateGroups} groups of duplicate skills that can be merged to clean up your profile. Use the "Merge Duplicates" button or press "M" to merge them.`,
              type: 'info',
              buttonLabel: 'Got it'
            });
          }, 1000); // Delay to avoid conflicts with other notifications
        }
      }
    } else {
      setDuplicateStats(null);
      setDuplicateGroups([]);
    }
  }, [userSkills]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyPress = (e) => {
      // Only activate shortcuts when not typing in an input or editable element
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'SELECT' || e.target.tagName === 'TEXTAREA' || e.target.isContentEditable) {
        return;
      }

      switch (e.key.toLowerCase()) {
        case 'g':
          setViewMode('grid');
          break;
        case 'l':
          setViewMode('list');
          break;
        case 'c':
          if (searchTerm || selectedCategory !== 'all' || filterBy !== 'all') {
            setSearchTerm('');
            setSelectedCategory('all');
            setFilterBy('all');
          }
          break;
        case 'a':
          setFilterBy('added');
          break;
        case 'u':
          setFilterBy('unrated');
          break;
        case 'm':
          // Show merge duplicates modal if duplicates exist
          if (duplicateStats && duplicateStats.duplicateGroups > 0) {
            setShowDuplicateModal(true);
          }
          break;
        case '/': {
          e.preventDefault();
          // Focus search input
          const searchInput = document.querySelector('input[placeholder="Search skills..."]');
          if (searchInput) {
            searchInput.focus();
          }
          break;
        }
        default:
          break;
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
  }, [searchTerm, selectedCategory, filterBy, duplicateStats]);

  // Get all available skills from categories and include custom skills
  const getAllSkills = () => {
    // Start with predefined skills from categories
    const allSkills = [];
    const predefinedSkillNames = new Set();
    
    // Add predefined skills
    Object.entries(PROFILE_CONSTANTS.SKILL_CATEGORIES).forEach(([category, skills]) => {
      skills.forEach(skill => {
        allSkills.push({ name: skill, category });
        predefinedSkillNames.add(skill.toLowerCase());
      });
    });
    
    // Add custom skills from userSkills that aren't in predefined categories
    if (userSkills && userSkills.length > 0) {
      userSkills.forEach(userSkill => {
        const skillName = getSafeSkillName(userSkill);
        // Only add if it's not already in the predefined skills
        if (!predefinedSkillNames.has(skillName.toLowerCase())) {
          allSkills.push({
            name: skillName,
            category: userSkill.category || 'Custom',
            isCustom: true
          });
        }
      });
    }
    
    return allSkills;
  };

  // Memoize availableSkills to update when userSkills changes
  const availableSkills = useMemo(() => {
    return getAllSkills();
  }, [userSkills]);

  // Memoized filtered and sorted skills for performance
  const filteredSkills = useMemo(() => {
    // Build lookup Map once: O(m) — avoids O(n*m) find() calls inside filter/sort
    const userSkillMap = new Map(
      userSkills.map(s => [getSafeSkillName(s).toLowerCase(), s])
    );

    let filtered = availableSkills.filter(skill => {
      const matchesSearch = skill.name.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCategory = selectedCategory === 'all' || skill.category === selectedCategory;
      
      // O(1) lookup
      const userSkill = userSkillMap.get(skill.name.toLowerCase());
      let matchesFilter = true;
      
      switch (filterBy) {
        case 'added':
          matchesFilter = !!userSkill;
          break;
        case 'notAdded':
          matchesFilter = !userSkill;
          break;
        case 'rated':
          matchesFilter = !!userSkill && userSkill.rating > 0;
          break;
        case 'unrated':
          matchesFilter = !!userSkill && (!userSkill.rating || userSkill.rating === 0);
          break;
        default:
          matchesFilter = true;
      }
      
      return matchesSearch && matchesCategory && matchesFilter;
    });

    // Apply sorting — O(1) lookups via Map
    filtered.sort((a, b) => {
      const userSkillA = userSkillMap.get(a.name.toLowerCase());
      const userSkillB = userSkillMap.get(b.name.toLowerCase());
      
      let compareValue = 0;
      
      switch (sortBy) {
        case 'name':
          compareValue = a.name.localeCompare(b.name);
          break;
        case 'category':
          compareValue = a.category.localeCompare(b.category);
          break;
        case 'rating': {
          const ratingA = userSkillA?.rating || 0;
          const ratingB = userSkillB?.rating || 0;
          compareValue = ratingB - ratingA; // Higher ratings first by default
          break;
        }
        case 'added': {
          const addedA = !!userSkillA;
          const addedB = !!userSkillB;
          compareValue = addedB - addedA; // Added skills first by default
          break;
        }
        default:
          compareValue = a.name.localeCompare(b.name);
      }
      
      return sortOrder === 'desc' ? -compareValue : compareValue;
    });

    return filtered;
  }, [availableSkills, searchTerm, selectedCategory, filterBy, userSkills, sortBy, sortOrder]);

  // Handle adding/removing skills - Memoized for performance with enhanced name handling
  const handleAddSkill = useCallback((skillName, category) => {
    // Ensure skill name is properly sanitized
    const safeName = typeof skillName === 'string' ? skillName : getSafeSkillName(skillName);
    
    // Find existing skill by name (case insensitive for better matching)
    const existingSkill = userSkills.find(s => {
      const existingName = getSafeSkillName(s);
      return existingName.toLowerCase() === safeName.toLowerCase();
    });
    
    if (existingSkill) {
      // Remove skill - use the existing skill name to ensure exact match
      setUserSkills(prev => prev.filter(s => getSafeSkillName(s) !== getSafeSkillName(existingSkill)));
    } else {
      // Add skill with default rating and empty experience
      setUserSkills(prev => [...prev, { 
        name: safeName, 
        category, 
        rating: 3, 
        experience: '',
        type: 'upskills' // Mark as upskills format for future processing
      }]);
    }
  }, [userSkills]);

  // Handle rating skills - Memoized for performance with enhanced name handling
  const handleRateSkill = useCallback((skillName, rating) => {
    // Ensure skill name is properly sanitized
    const safeName = typeof skillName === 'string' ? skillName : getSafeSkillName(skillName);
    
    setUserSkills(prev => 
      prev.map(skill => {
        const currentName = getSafeSkillName(skill);
        if (currentName.toLowerCase() === safeName.toLowerCase()) {
          // Update rating while preserving all other properties
          return {
            ...skill,
            rating,
            // If this is a high rating (4-5) and there's no experience yet, add a placeholder
            experience: rating >= 4 && (!skill.experience || skill.experience.trim() === '') ?
              'Please add your experience with this skill' :
              skill.experience
          };
        }
        return skill;
      })
    );
    
    // If rating is high, show a hint about adding experience (but only once per session)
    if (rating >= 4 && !notificationsShownRef.current.highRatingInfo) {
      // Mark as shown to prevent multiple notifications
      notificationsShownRef.current.highRatingInfo = true;
      
      // Use setTimeout to avoid state updates during render
      setTimeout(() => {
        showInfo({
          title: 'Experience Required',
          message: 'For skills rated 4-5 stars, please add a detailed experience description (minimum 180 characters).',
          type: 'info',
          buttonLabel: 'Got it'
        });
      }, 0);
    }
  }, [showInfo]);

  // Handle updating skill experience - Memoized for performance with enhanced name handling
  const handleUpdateExperience = useCallback((skillName, experience) => {
    // Ensure skill name is properly sanitized
    const safeName = typeof skillName === 'string' ? skillName : getSafeSkillName(skillName);
    
    setUserSkills(prev => 
      prev.map(skill => {
        const currentName = getSafeSkillName(skill);
        if (currentName.toLowerCase() === safeName.toLowerCase()) {
          // Check if this is a high-rated skill with insufficient experience
          const needsMoreExperience = skill.rating >= 4 && experience.length < 180;
          
          // Update experience while preserving all other properties
          return {
            ...skill,
            experience,
            // Add validation flag for UI feedback
            hasValidationIssue: needsMoreExperience
          };
        }
        return skill;
      })
    );
  }, []);

  // Validate if skill can be added based on rating and experience
  const validateSkillAddition = (skillName, rating, experience) => {
    if (rating >= 4 && (!experience || experience.trim().length < 180)) {
      return {
        isValid: false,
        message: `Skills rated 4-5 stars require at least 180 characters of experience. Current: ${experience?.length || 0}/180`
      };
    }
    return { isValid: true, message: '' };
  };

  // Memoized validation functions for performance with enhanced name handling
  const canAddSkill = useCallback((skillName) => {
    // Ensure skill name is properly sanitized
    const safeName = typeof skillName === 'string' ? skillName : getSafeSkillName(skillName);
    
    // Find skill by name (case insensitive for better matching)
    const userSkill = userSkills.find(s => {
      const currentName = getSafeSkillName(s);
      return currentName.toLowerCase() === safeName.toLowerCase();
    });
    
    if (!userSkill) return true; // Can always add new skills initially
    
    const validation = validateSkillAddition(safeName, userSkill.rating, userSkill.experience);
    return validation.isValid;
  }, [userSkills]);

  const getSkillValidationMessage = useCallback((skillName) => {
    // Ensure skill name is properly sanitized
    const safeName = typeof skillName === 'string' ? skillName : getSafeSkillName(skillName);
    
    // Find skill by name (case insensitive for better matching)
    const userSkill = userSkills.find(s => {
      const currentName = getSafeSkillName(s);
      return currentName.toLowerCase() === safeName.toLowerCase();
    });
    
    if (!userSkill || userSkill.rating < 4) return '';
    
    const validation = validateSkillAddition(safeName, userSkill.rating, userSkill.experience);
    return validation.isValid ? '' : validation.message;
  }, [userSkills]);

  const hasValidationIssues = useCallback((skillName) => {
    // Ensure skill name is properly sanitized
    const safeName = typeof skillName === 'string' ? skillName : getSafeSkillName(skillName);
    
    // Find skill by name (case insensitive for better matching)
    const userSkill = userSkills.find(s => {
      const currentName = getSafeSkillName(s);
      return currentName.toLowerCase() === safeName.toLowerCase();
    });
    
    if (!userSkill) return false;
    
    return userSkill.rating >= 4 && (!userSkill.experience || userSkill.experience.trim().length < 180);
  }, [userSkills]);

  // Check if there are any skills with validation issues - enhanced to handle all edge cases
  const hasAnyValidationIssues = useCallback(() => {
    // Count skills with validation issues
    const skillsWithIssues = userSkills.filter(skill => {
      // Skip skills without rating (shouldn't happen but just in case)
      if (typeof skill.rating !== 'number') return false;
      
      // Check if this is a high-rated skill
      const isHighRated = skill.rating >= 4;
      
      // Check if experience is missing or too short
      const experienceText = typeof skill.experience === 'string' ? skill.experience.trim() : '';
      const hasInsufficientExperience = experienceText.length < 180;
      
      // Only high-rated skills with insufficient experience have validation issues
      return isHighRated && hasInsufficientExperience;
    });
    
    // Return true if any skills have validation issues
    return skillsWithIssues.length > 0;
  }, [userSkills]);
  
  // Get a list of skills with validation issues for detailed feedback
  const getSkillsWithValidationIssues = useCallback(() => {
    return userSkills.filter(skill => 
      skill.rating >= 4 && (!skill.experience || skill.experience.trim().length < 180)
    ).map(skill => getSafeSkillName(skill));
  }, [userSkills]);

  // Save skills to profile with enhanced data cleaning, normalization and verification
  const handleSaveSkills = async () => {
    // Check for validation issues before saving
    if (hasAnyValidationIssues()) {
      // Get list of skills with validation issues
      const skillsWithIssues = getSkillsWithValidationIssues();
      
      // Create a more detailed message
      let message = 'Please complete the experience section for all skills rated 4-5 stars (minimum 180 characters) before saving.';
      
      // Add specific skills that need attention if not too many
      if (skillsWithIssues.length > 0 && skillsWithIssues.length <= 5) {
        message += '\n\nSkills needing attention:\n• ' + skillsWithIssues.join('\n• ');
      } else if (skillsWithIssues.length > 5) {
        message += `\n\n${skillsWithIssues.length} skills need attention, including:\n• ` + 
          skillsWithIssues.slice(0, 3).join('\n• ') + 
          `\n• ...and ${skillsWithIssues.length - 3} more`;
      }
      
      // Show validation message using setTimeout
      setTimeout(() => {
        showInfo({
          title: 'Validation Required',
          message: message,
          type: 'warning'
        });
      }, 0);
      return;
    }

    try {
      // Show loading indicator using setTimeout to avoid potential render issues
      setTimeout(() => {
        showInfo({
          title: 'Saving Skills',
          message: 'Processing and saving your skills...',
          type: 'info'
        });
      }, 0);
      
      // Process skills data to clean and normalize before saving
      const { skills: normalizedSkills, hasRawJsonIssues, stats } = processSkillsData(userSkills);
      
      // Log stats if there were issues found
      if (hasRawJsonIssues) {
        console.log('Fixed raw JSON issues in skills data before saving:', stats);
      }
      
      // Ensure each skill has a unique ID and proper type
      const finalSkills = normalizedSkills.map((skill, index) => {
        // If skill already has an ID, keep it
        const skillId = skill.id || `skill-${Date.now()}-${index}-${Math.random().toString(36).substr(2, 5)}`;
        
        return {
          ...skill,
          id: skillId,
          type: skill.type || 'upskills',
          // Ensure these fields are properly set
          name: getSafeSkillName(skill),
          category: typeof skill.category === 'string' ? skill.category : 'Administrative',
          rating: typeof skill.rating === 'number' ? skill.rating : 3,
          experience: typeof skill.experience === 'string' ? skill.experience : ''
        };
      });
      
      // Save the normalized skills to the profile
      const result = await updateProfile({ skills: finalSkills });
      
      // Verify the update was successful
      if (!result?.success) {
        throw new Error(result?.error || 'Unknown error during profile update');
      }
      
      // Refresh the user skills with the saved data
      setUserSkills(finalSkills);
      
      // Close any open info modal
      closeInfo();
      
      // Show appropriate success message using setTimeout
      setTimeout(() => {
        if (hasRawJsonIssues) {
          showInfo({
            title: 'Skills Saved with Fixes',
            message: `Skills saved successfully! Fixed ${stats.recoveredFromRawJson} skills with data format issues.`,
            type: 'success'
          });
        } else {
          showInfo({
            title: 'Success',
            message: `${finalSkills.length} skills saved successfully to your profile!`,
            type: 'success'
          });
        }
      }, 0);
    } catch (error) {
      console.error('Failed to save skills:', error);
      // Close any open info modal
      closeInfo();
      
      // Show error message using setTimeout
      setTimeout(() => {
        showInfo({
          title: 'Error Saving Skills',
          message: `Failed to save skills: ${error.message || 'Unknown error'}. Please try again.`,
          type: 'error'
        });
      }, 0);
    }
  };

  // Function to open the custom skill modal
  const openCustomSkillModal = () => {
    setShowCustomSkillModal(true);
  };

  // Handle merging duplicates
  const handleMergeDuplicates = useCallback(async () => {
    if (!userSkills || userSkills.length === 0 || duplicateGroups.length === 0) {
      return;
    }

    setMergingInProgress(true);
    
    try {
      // Show progress info
      setTimeout(() => {
        showInfo({
          title: 'Merging Duplicates',
          message: `Processing ${duplicateStats.duplicateSkills} duplicate skills across ${duplicateStats.duplicateGroups} groups...`,
          type: 'info'
        });
      }, 0);

      // Process and merge duplicates
      const result = processAndMergeDuplicates(userSkills);
      
      // Update userSkills with merged results
      setUserSkills(result.skills);
      
      // Close any open modals
      setShowDuplicateModal(false);
      closeInfo();
      
      // Show success message
      setTimeout(() => {
        showInfo({
          title: 'Duplicates Merged Successfully',
          message: `Merged ${result.duplicatesMerged} duplicate skills. Your profile now has ${result.totalSkillsAfter} unique skills (reduced from ${result.totalSkillsBefore}).`,
          type: 'success',
          buttonLabel: 'Great!'
        });
      }, 0);
      
    } catch (error) {
      console.error('Error merging duplicates:', error);
      closeInfo();
      
      setTimeout(() => {
        showInfo({
          title: 'Error Merging Duplicates',
          message: `Failed to merge duplicates: ${error.message || 'Unknown error'}. Please try again.`,
          type: 'error'
        });
      }, 0);
    } finally {
      setMergingInProgress(false);
    }
  }, [userSkills, duplicateGroups, duplicateStats, showInfo, closeInfo]);

  // Check for similar skills when adding new skills
  const checkForSimilarSkills = useCallback((newSkillName) => {
    const similarSkills = userSkills.filter(skill => {
      const existingName = getSafeSkillName(skill);
      return existingName.toLowerCase() !== newSkillName.toLowerCase() && 
             areSkillsSimilar(existingName, newSkillName, 0.7); // Lower threshold for suggestions
    });
    
    return similarSkills.map(skill => getSafeSkillName(skill));
  }, [userSkills]);

  // Add custom skill with enhanced handling
  const handleAddCustomSkill = () => {
    if (customSkill.name.trim()) {
      // Sanitize the skill name to remove special characters
      const skillName = sanitizeSkillName(customSkill.name.trim());
      
      const existingSkill = userSkills.find(s => {
        const currentName = getSafeSkillName(s);
        return currentName.toLowerCase() === skillName.toLowerCase();
      });
      
      if (existingSkill) {
        // Show warning that skill already exists using setTimeout
        setTimeout(() => {
          showInfo({
            title: 'Skill Already Exists',
            message: `A skill with the name "${skillName}" already exists in your profile.`,
            type: 'warning'
          });
        }, 0);
        return;
      }
      
      // Check for similar skills
      const similarSkills = checkForSimilarSkills(skillName);
      if (similarSkills.length > 0) {
        setTimeout(() => {
          showInfo({
            title: 'Similar Skills Found',
            message: `Found similar skills: ${similarSkills.join(', ')}. Consider if this is truly a different skill or if you should update an existing one.`,
            type: 'info',
            buttonLabel: 'Add Anyway'
          });
        }, 0);
      }
      
      // Create new skill with upskills format
      const newSkill = {
        name: skillName,
        category: customSkill.category,
        rating: 3,
        experience: '',
        type: 'upskills', // Mark as upskills format for future processing
        id: `custom-${Date.now()}-${Math.random().toString(36).substr(2, 5)}` // Add unique ID
      };
      
      // Add to user skills
      setUserSkills(prev => [...prev, newSkill]);
      
      // Reset form and close modal
      setCustomSkill({ name: '', category: 'Administrative' });
      setShowCustomSkillModal(false);
      
      // Show success message using setTimeout
      setTimeout(() => {
        showInfo({
          title: 'Skill Added',
          message: `"${skillName}" has been added to your skills. Don't forget to save your changes!`,
          type: 'success'
        });
      }, 0);
    }
  };

  const categories = ['all', ...Object.keys(PROFILE_CONSTANTS.SKILL_CATEGORIES)];

  return (
    <>
      <_Helmet>
        <title>Upskill Your Profile - VA Sprouts</title>
        <meta name="description" content="Add and rate your skills to enhance your VA profile" />
      </_Helmet>

      <_VAHeaderNav />
      
      <div className="min-h-screen bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-text-primary mb-2">
                  Upskill Your Profile
                </h1>
                <p className="text-text-secondary">
                  Add skills to your profile and rate your proficiency to attract better opportunities
                </p>
              </div>
              
              {/* Keyboard Shortcuts Help */}
              <div className="hidden lg:block">
                <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                  <p className="text-xs font-medium text-gray-700 mb-2">Keyboard Shortcuts:</p>
                  <div className="grid grid-cols-2 gap-2 text-xs text-gray-600">
                    <div><kbd className="px-1 py-0.5 bg-white border rounded text-xs">G</kbd> Grid view</div>
                    <div><kbd className="px-1 py-0.5 bg-white border rounded text-xs">L</kbd> List view</div>
                    <div><kbd className="px-1 py-0.5 bg-white border rounded text-xs">A</kbd> Added skills</div>
                    <div><kbd className="px-1 py-0.5 bg-white border rounded text-xs">U</kbd> Unrated skills</div>
                    <div><kbd className="px-1 py-0.5 bg-white border rounded text-xs">C</kbd> Clear filters</div>
                    <div><kbd className="px-1 py-0.5 bg-white border rounded text-xs">M</kbd> Merge duplicates</div>
                    <div><kbd className="px-1 py-0.5 bg-white border rounded text-xs">/</kbd> Search</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-3">
              {/* Search and Filters */}
              <div className="bg-white rounded-xl p-6 border border-border shadow-sm mb-6">
                <div className="flex flex-col gap-4">
                  {/* Top Row: Search, View Toggle, Custom Skill Button */}
                  <div className="flex flex-col sm:flex-row gap-4">
                    <div className="flex-1">
                      <_Input
                        placeholder="Search skills..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full"
                      />
                    </div>
                    
                    {/* View Mode Toggle */}
                    <div className="flex rounded-lg border border-gray-300 overflow-hidden">
                      <button
                        onClick={() => setViewMode('grid')}
                        className={`px-3 py-2 text-sm font-medium  ${
                          viewMode === 'grid'
                            ? 'bg-primary text-white'
                            : 'bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        <_Icon name="Grid" size={16} className="inline mr-1" />
                        Grid
                      </button>
                      <button
                        onClick={() => setViewMode('list')}
                        className={`px-3 py-2 text-sm font-medium  ${
                          viewMode === 'list'
                            ? 'bg-primary text-white'
                            : 'bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        <_Icon name="List" size={16} className="inline mr-1" />
                        List
                      </button>
                    </div>
                    
                    <_Button
                      variant="outline"
                      onClick={openCustomSkillModal}
                      iconName="Plus"
                    >
                      Add Skill
                    </_Button>
                    
                    {/* Duplicate Detection Button */}
                    {duplicateStats && duplicateStats.duplicateGroups > 0 && (
                      <_Button
                        variant="secondary"
                        onClick={() => setShowDuplicateModal(true)}
                        iconName="GitMerge"
                        className="relative"
                      >
                        Merge Duplicates
                        <span className="ml-1 px-2 py-0.5 text-xs bg-red-100 text-red-800 rounded-full">
                          {duplicateStats.duplicateGroups}
                        </span>
                      </_Button>
                    )}
                  </div>

                  {/* Second Row: Sort and Filter Controls */}
                  <div className="flex flex-col sm:flex-row gap-4 pt-4 border-t border-gray-200">
                    {/* Sort Controls */}
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-700">Sort by:</span>
                      <select
                        value={sortBy}
                        onChange={(e) => setSortBy(e.target.value)}
                        className="text-sm border border-gray-300 rounded-md px-2 py-1"
                      >
                        <option value="name">Name</option>
                        <option value="category">Category</option>
                        <option value="rating">Rating</option>
                        <option value="added">Added Status</option>
                      </select>
                      
                      <button
                        onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                        className="p-1 text-gray-500 hover:text-gray-700 "
                        title={`Sort ${sortOrder === 'asc' ? 'Descending' : 'Ascending'}`}
                      >
                        <_Icon name={sortOrder === 'asc' ? 'ArrowUp' : 'ArrowDown'} size={16} />
                      </button>
                    </div>

                    {/* Filter Controls */}
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-700">Filter:</span>
                      <select
                        value={filterBy}
                        onChange={(e) => setFilterBy(e.target.value)}
                        className="text-sm border border-gray-300 rounded-md px-2 py-1"
                      >
                        <option value="all">All Skills</option>
                        <option value="added">Added Only</option>
                        <option value="notAdded">Not Added</option>
                        <option value="rated">Rated</option>
                        <option value="unrated">Unrated</option>
                      </select>
                    </div>

                    {/* Results Count */}
                    <div className="flex items-center text-sm text-gray-500 ml-auto">
                      {filteredSkills.length} skill{filteredSkills.length !== 1 ? 's' : ''} found
                    </div>
                  </div>

                  {/* Category Tabs */}
                  <div className="flex flex-wrap gap-2 pt-4 border-t border-gray-200">
                    {categories.map(category => (
                      <button
                        key={category}
                        onClick={() => setSelectedCategory(category)}
                        className={`px-4 py-2 rounded-lg text-sm font-medium  ${
                          selectedCategory === category
                            ? 'bg-primary text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {category === 'all' ? 'All Skills' : category}
                        {category !== 'all' && (
                          <span className="ml-1 text-xs opacity-75">
                            ({availableSkills.filter(s => s.category === category).length})
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Skills Display */}
              {viewMode === 'grid' ? (
                // Grid View
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                  {filteredSkills.map(skill => {
                    const userSkill = userSkills.find(s => {
                      const currentName = getSafeSkillName(s);
                      return currentName.toLowerCase() === skill.name.toLowerCase();
                    });
                    return (
                      <SkillCard
                        key={skill.name}
                        skill={skill.name}
                        category={skill.category}
                        isAdded={!!userSkill}
                        userRating={userSkill?.rating || 0}
                        userExperience={userSkill?.experience || ''}
                        onAddSkill={handleAddSkill}
                        onRateSkill={handleRateSkill}
                        onUpdateExperience={handleUpdateExperience}
                        canAdd={canAddSkill(skill.name)}
                        validationMessage={getSkillValidationMessage(skill.name)}
                        hasIssues={hasValidationIssues(skill.name)}
                        isCustom={skill?.isCustom || false}
                      />
                    );
                  })}
                </div>
              ) : (
                // List View
                <div className="space-y-3">
                  {filteredSkills.map(skill => {
                    const userSkill = userSkills.find(s => {
                      const currentName = getSafeSkillName(s);
                      return currentName.toLowerCase() === skill.name.toLowerCase();
                    });
                    return (
                      <SkillListItem
                        key={skill.name}
                        skill={skill.name}
                        category={skill.category}
                        isAdded={!!userSkill}
                        userRating={userSkill?.rating || 0}
                        userExperience={userSkill?.experience || ''}
                        onAddSkill={handleAddSkill}
                        onRateSkill={handleRateSkill}
                        onUpdateExperience={handleUpdateExperience}
                        canAdd={canAddSkill(skill.name)}
                        validationMessage={getSkillValidationMessage(skill.name)}
                        hasIssues={hasValidationIssues(skill.name)}
                        isCustom={skill?.isCustom || false}
                      />
                    );
                  })}
                </div>
              )}

              {filteredSkills.length === 0 && (
                <div className="text-center py-12">
                  <_Icon name="Search" size={48} className="text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No skills found</h3>
                  <p className="text-gray-500 mb-4">
                    {searchTerm || selectedCategory !== 'all' || filterBy !== 'all' 
                      ? 'Try adjusting your search, category, or filter settings'
                      : 'No skills available'
                    }
                  </p>
                  {(searchTerm || selectedCategory !== 'all' || filterBy !== 'all') && (
                    <_Button
                      variant="outline"
                      onClick={() => {
                        setSearchTerm('');
                        setSelectedCategory('all');
                        setFilterBy('all');
                      }}
                    >
                      Clear Filters
                    </_Button>
                  )}
                </div>
              )}
            </div>

            {/* Sidebar - My Skills */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-xl p-6 border border-border shadow-sm sticky top-32">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-text-primary">
                    My Skills ({userSkills.length})
                  </h3>
                  {viewMode === 'list' && userSkills.length > 0 && (
                    <button
                      onClick={() => setViewMode('grid')}
                      className="text-sm text-primary hover:text-primary-600"
                    >
                      View Grid
                    </button>
                  )}
                </div>
                
                {/* Quick Stats */}
                {userSkills.length > 0 && (
                  <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div>
                        <span className="text-gray-600">Avg Rating:</span>
                        <div className="font-medium">
                          {userSkills.length > 0 
                            ? (userSkills.reduce((sum, skill) => sum + (skill.rating || 0), 0) / userSkills.length).toFixed(1)
                            : '0.0'
                          } / 5.0
                        </div>
                      </div>
                      <div>
                        <span className="text-gray-600">Categories:</span>
                        <div className="font-medium">
                          {new Set(userSkills.map(s => s.category)).size}
                        </div>
                      </div>
                      {duplicateStats && duplicateStats.duplicateGroups > 0 && (
                        <>
                          <div>
                            <span className="text-gray-600">Duplicates:</span>
                            <div className="font-medium text-red-600">
                              {duplicateStats.duplicateGroups} groups
                            </div>
                          </div>
                          <div>
                            <span className="text-gray-600">Can Save:</span>
                            <div className="font-medium text-green-600">
                              {duplicateStats.potentialSavings} skills
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                )}
                
                {userSkills.length === 0 ? (
                  <div className="text-center py-6">
                    <_Icon name="Plus" size={32} className="text-gray-300 mx-auto mb-2" />
                    <p className="text-text-muted text-sm mb-3">
                      Start adding skills to enhance your profile
                    </p>
                    <p className="text-xs text-gray-400">
                      Tip: Use the {viewMode === 'grid' ? 'cards above' : 'list above'} to browse and add skills
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3 mb-6">
                    {userSkills.slice(0, 8).map(skill => {
                      // Safely extract skill name using the imported utility function
                      const skillName = getSafeSkillName(skill);
                      const skillCategory = typeof skill.category === 'string' ? skill.category : 'Other';
                      
                      // Generate a safe key that won't cause React warnings
                      const key = `skill-${skillName.replace(/\W/g, '')}-${Math.random().toString(36).substr(2, 5)}`;
                      
                      return (
                        <div key={key} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-text-primary truncate">
                              {skillName}
                            </p>
                            <div className="flex items-center gap-2 mt-1">
                              <StarRating rating={skill.rating} readOnly size={12} />
                              <span className="text-xs text-gray-500">
                                {skillCategory}
                              </span>
                            </div>
                          </div>
                          <button
                            onClick={() => handleAddSkill(skillName)}
                            className="text-gray-400 hover:text-red-500 ml-2 p-1"
                            title="Remove skill"
                          >
                            <_Icon name="X" size={14} />
                          </button>
                        </div>
                      );
                    })}
                    
                    {userSkills.length > 8 && (
                      <div className="text-center">
                        <button
                          onClick={() => {
                            setFilterBy('added');
                            setViewMode('list');
                          }}
                          className="text-xs text-primary hover:text-primary-600"
                        >
                          View all {userSkills.length} skills
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {userSkills.length > 0 && (
                  <div className="space-y-3">
                    {/* Validation Warning */}
                    {hasAnyValidationIssues() && (
                      <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                        <div className="flex items-start gap-2">
                          <_Icon name="AlertTriangle" size={16} className="text-amber-500 mt-0.5 flex-shrink-0" />
                          <div>
                            <p className="text-sm font-medium text-amber-800 mb-1">Incomplete Skills Detected</p>
                            <p className="text-xs text-amber-700">
                              Some skills rated 4-5 stars need experience descriptions (180+ characters) before saving.
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    <_Button
                      variant="primary"
                      fullWidth
                      onClick={handleSaveSkills}
                      iconName="Save"
                      disabled={hasAnyValidationIssues()}
                    >
                      Save to Profile
                    </_Button>
                  </div>
                )}

                {/* Quick Filter Buttons */}
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <p className="text-sm font-medium text-gray-700 mb-2">Quick Filters:</p>
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => setFilterBy('added')}
                      className={`px-2 py-1 text-xs rounded ${
                        filterBy === 'added' 
                          ? 'bg-primary text-white' 
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      My Skills
                    </button>
                    <button
                      onClick={() => setFilterBy('notAdded')}
                      className={`px-2 py-1 text-xs rounded ${
                        filterBy === 'notAdded' 
                          ? 'bg-primary text-white' 
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      Available
                    </button>
                    <button
                      onClick={() => setFilterBy('unrated')}
                      className={`px-2 py-1 text-xs rounded ${
                        filterBy === 'unrated' 
                          ? 'bg-primary text-white' 
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      Unrated
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Custom Skill Modal */}
      {(showCustomSkillModal || false) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Add Custom Skill</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Skill Name</label>
                <_Input
                  value={customSkill.name}
                  onChange={(e) => {
                    const sanitizedValue = sanitizeSkillName(e.target.value);
                    setCustomSkill(prev => ({ ...prev, name: sanitizedValue }));
                  }}
                  placeholder="Enter skill name"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Category</label>
                <select
                  value={customSkill.category}
                  onChange={(e) => setCustomSkill(prev => ({ ...prev, category: e.target.value }))}
                  className="w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
                >
                  {Object.keys(PROFILE_CONSTANTS.SKILL_CATEGORIES).map(category => (
                    <option key={category} value={category}>
                      {category}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <_Button
                variant="outline"
                onClick={() => setShowCustomSkillModal(false)}
                className="flex-1"
              >
                Cancel
              </_Button>
              <_Button
                variant="primary"
                onClick={() => {
                  handleAddCustomSkill();
                }}
                className="flex-1"
                disabled={!customSkill.name.trim()}
              >
                Add Skill
              </_Button>
            </div>
          </div>
        </div>
      )}

      {/* Duplicate Skills Modal */}
      {showDuplicateModal && duplicateStats && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Merge Duplicate Skills</h3>
              <button
                onClick={() => setShowDuplicateModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <_Icon name="X" size={20} />
              </button>
            </div>
            
            {/* Duplicate Statistics */}
            <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-center gap-2 mb-2">
                <_Icon name="Info" size={16} className="text-blue-600" />
                <h4 className="font-medium text-blue-800">Duplicate Detection Results</h4>
              </div>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-600">Total Skills:</span>
                  <div className="font-medium text-gray-900">{duplicateStats.totalSkills}</div>
                </div>
                <div>
                  <span className="text-gray-600">Duplicate Groups:</span>
                  <div className="font-medium text-blue-600">{duplicateStats.duplicateGroups}</div>
                </div>
                <div>
                  <span className="text-gray-600">Duplicate Skills:</span>
                  <div className="font-medium text-red-600">{duplicateStats.duplicateSkills}</div>
                </div>
                <div>
                  <span className="text-gray-600">After Merge:</span>
                  <div className="font-medium text-green-600">{duplicateStats.uniqueSkills}</div>
                </div>
              </div>
            </div>

            {/* Duplicate Groups Preview */}
            <div className="mb-6">
              <h4 className="font-medium text-gray-800 mb-3">Skills to be Merged:</h4>
              <div className="space-y-3 max-h-64 overflow-y-auto">
                {duplicateGroups.map((group, groupIndex) => (
                  <div key={groupIndex} className="p-3 bg-gray-50 rounded-lg border">
                    <div className="flex items-center gap-2 mb-2">
                      <_Icon name="GitMerge" size={14} className="text-gray-500" />
                      <span className="text-sm font-medium text-gray-700">
                        Group {groupIndex + 1} ({group.length} skills)
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {group.map((item, skillIndex) => {
                        const skillName = getSafeSkillName(item.skill);
                        const isObject = typeof item.skill === 'object';
                        return (
                          <div
                            key={skillIndex}
                            className={`px-2 py-1 text-xs rounded-full ${
                              skillIndex === 0
                                ? 'bg-green-100 text-green-800 border border-green-200'
                                : 'bg-gray-100 text-gray-700 border border-gray-200'
                            }`}
                          >
                            {skillName}
                            {isObject && (
                              <span className="ml-1">
                                ⭐{item.skill.rating || 0}
                              </span>
                            )}
                            {skillIndex === 0 && (
                              <span className="ml-1 text-green-600">🏆</span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Warning */}
            <div className="mb-6 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-start gap-2">
                <_Icon name="AlertTriangle" size={16} className="text-amber-500 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-amber-800 mb-1">Before You Proceed</p>
                  <p className="text-xs text-amber-700">
                    This will merge similar skills, keeping the best rating and experience. The skill marked with 🏆 
                    will be kept as the primary skill. This action cannot be undone automatically.
                  </p>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3">
              <_Button
                variant="outline"
                onClick={() => setShowDuplicateModal(false)}
                className="flex-1"
                disabled={mergingInProgress}
              >
                Cancel
              </_Button>
              <_Button
                variant="primary"
                onClick={handleMergeDuplicates}
                className="flex-1"
                disabled={mergingInProgress}
                iconName={mergingInProgress ? "Loader" : "GitMerge"}
              >
                {mergingInProgress ? 'Merging...' : `Merge ${duplicateStats.duplicateGroups} Groups`}
              </_Button>
            </div>
          </div>
        </div>
      )}

      {/* Info Modal */}
      {infoModal && (
        <InfoModal
          title={infoModal.title}
          message={infoModal.message}
          type={infoModal.type}
          buttonLabel={infoModal.buttonLabel}
          onClose={closeInfo}
        />
      )}
    </>
  );
};

export default UpSkillsPage;
